// Considered: PerspectiveFilterMolecule — mirrored its collapsible-section IA
// (relationship-filter-ui-brief.md §5) but not its implementation: it imports
// PrimeReact directly and hardcodes #377FC4, both standards violations this
// molecule must not repeat. See spec Reuse Audit.
//
// The per-row editing body (relationship pick, direction, related spheres,
// count) has been extracted to `RelationshipValueEditorMolecule` (EAINT-725
// Phase 2, mirroring the ArcFilterMolecule → ArcValueEditorMolecule
// precedent) so the list and a group-tree leaf share one editor instead of
// two implementations of the same row.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type { RelationshipFilter, UnifiedFilters } from 'types/filters';
import type { SchemaRelationship } from 'types/schema';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import RelationshipValueEditorMolecule from '../RelationshipValueEditor/RelationshipValueEditorMolecule';
import './RelationshipFilterMolecule.scss';

export interface RelationshipOption {
  systemName: string;
  label: string;
  description?: string;
  relationshipType?: SchemaRelationship['relationship_type'];
  cardinality?: SchemaRelationship['cardinality'];
  /** Scopes the related-sphere picker — a discovery hint, not enforcement. */
  targetFilter?: UnifiedFilters;
}

export interface RelationshipFilterMoleculeProps {
  /** Available relationships for this filter context (schema-derived, deduped). */
  availableRelationships: RelationshipOption[];
  /** Current filter state — the canonical persisted `RelationshipFilter[]` shape (no editor-only transform needed). */
  value?: RelationshipFilter[];
  /** Callback when filter state changes */
  onChange?: (filters: RelationshipFilter[]) => void;
  /** Whether to show the collapsible section */
  collapsible?: boolean;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
  readOnly?: boolean;
  className?: string;
}

const RelationshipFilterMolecule: React.FC<RelationshipFilterMoleculeProps> = ({
  availableRelationships,
  value = [],
  onChange,
  collapsible = true,
  defaultCollapsed = true,
  readOnly = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const unusedRelationships = useMemo(
    () => availableRelationships.filter((r) => !value.some((f) => f.relationshipSystemName === r.systemName)),
    [availableRelationships, value]
  );

  const updateRow = useCallback(
    (index: number, next: RelationshipFilter) => {
      onChange?.(value.map((f, i) => (i === index ? next : f)));
    },
    [value, onChange]
  );

  const removeRow = useCallback(
    (index: number) => {
      onChange?.(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const handleAddRelationship = useCallback(
    (systemName: string) => {
      const row: RelationshipFilter = { relationshipSystemName: systemName, operator: 'in', relatedTo: [] };
      onChange?.([...value, row]);
    },
    [value, onChange]
  );

  const activeCount = value.length;

  return (
    <div className={`relationship-filter-molecule ${className}`}>
      {collapsible && (
        <button
          type="button"
          className="relationship-filter-molecule__header"
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="relationship-filter-molecule__header-content">
            <i className="pi pi-share-alt" />
            <span className="relationship-filter-molecule__title">
              {t('context.editor.relationshipFilters.title', { defaultValue: 'Relationship Filters' })}
            </span>
            {activeCount > 0 && <span className="relationship-filter-molecule__badge">{activeCount}</span>}
          </div>
          <i className={`pi ${collapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} />
        </button>
      )}

      {!collapsed && (
        <div className="relationship-filter-molecule__content">
          {value.map((filter, index) => (
            <div
              key={`${filter.relationshipSystemName}-${index}`}
              className="relationship-filter-molecule__row"
              data-testid={`relationship-row-${index}`}
            >
              <RelationshipValueEditorMolecule
                availableRelationships={availableRelationships}
                value={filter}
                onChange={(next) => updateRow(index, next)}
                onRemove={readOnly ? undefined : () => removeRow(index)}
                readOnly={readOnly}
                index={index}
              />
            </div>
          ))}

          {!readOnly && unusedRelationships.length > 0 && (
            <div className="relationship-filter-molecule__add-row">
              <DropdownAtom
                value={null}
                options={unusedRelationships.map((r) => ({ label: r.label, value: r.systemName }))}
                onChange={(e) => e.value && handleAddRelationship(e.value)}
                placeholder={t('context.editor.relationshipFilters.addRelationship', {
                  defaultValue: '+ Add relationship',
                })}
                filter
                className="relationship-filter-molecule__add-select"
                data-testid="relationship-add-select"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RelationshipFilterMolecule;
